// WCAG 2.x contrast ratio between two colors.
//
// Used by the whitelabel institution hub to decide whether a partner's
// configured brand color is legible on a given background, and to pick the
// NCFDD logo variant / fall back to a readable color when it is not.

interface Rgb {
  r: number
  g: number
  b: number
}

const parseHex = (input: string): Rgb | null => {
  if (typeof input !== 'string') return null

  let hex = input.trim().replace(/^#/, '').toLowerCase()

  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('')
  }

  if (!/^[0-9a-f]{6}$/.test(hex)) return null

  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16)
  }
}

const relativeLuminance = ({ r, g, b }: Rgb): number => {
  const channel = (value: number) => {
    const c = value / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

/**
 * Returns the WCAG contrast ratio (1–21) between two hex colors, or `null` when
 * either color is missing or cannot be parsed. Callers treat `null` as "no
 * usable color" and fall back to their stylesheet defaults.
 */
const contrastRatio = (a: string, b: string): number | null => {
  const rgbA = parseHex(a)
  const rgbB = parseHex(b)
  if (!rgbA || !rgbB) return null

  const lumA = relativeLuminance(rgbA)
  const lumB = relativeLuminance(rgbB)
  const lighter = Math.max(lumA, lumB)
  const darker = Math.min(lumA, lumB)

  return (lighter + 0.05) / (darker + 0.05)
}

export default contrastRatio
